import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faHeart,
  faUsers,
  faRocket,
  faShieldAlt,
  faHandshake,
  faBullseye,
  faStar
} from '@fortawesome/free-solid-svg-icons';
import Navbar from './Navbar';
import Footer from './Footer';
import { DEFAULT_TRIAL_PERIOD_DAYS } from '../config/config';

interface Value {
  icon: any;
  title: string;
  description: string;
}

const values: Value[] = [
  {
    icon: faLightbulb,
    title: 'Innovation',
    description: 'We constantly innovate to bring cutting-edge solutions that simplify business operations for home service professionals.'
  },
  {
    icon: faHeart,
    title: 'Customer First',
    description: 'Our customers are at the heart of everything we do. We build features they need and provide support when they need it.'
  },
  {
    icon: faShieldAlt,
    title: 'Reliability',
    description: 'We build software you can count on. Your business data is secure, and our platform is available when you need it.'
  },
  {
    icon: faHandshake,
    title: 'Transparency',
    description: 'Simple pricing, honest communication, and no hidden fees. What you see is what you get.'
  }
];

const About: React.FC = () => {
  return (
    <div>
      <Navbar />

      {/* Hero Section */}
      <section className="py-5" style={{
        background: 'linear-gradient(135deg, #074a5a 0%, #053642 100%)',
        color: 'white',
        minHeight: '40vh',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h1 className="display-3 fw-bold mb-4">About Defined Home Services</h1>
              <p className="lead mb-0">
                Empowering home service businesses with the tools they need to grow, manage, and succeed.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Story */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="display-5 fw-bold mb-4" style={{ color: '#074a5a' }}>
                Our Story
              </h2>
              <p className="lead mb-3">
                Defined Home Services was born from a simple observation: home service businesses were struggling with outdated, complex software that didn't fit their needs.
              </p>
              <p className="mb-3">
                We saw cleaning companies, contractors, and other service professionals juggling multiple tools, spreadsheets, and paper schedules just to run their businesses. There had to be a better way.
              </p>
              <p className="mb-3">
                So we built it. A comprehensive CRM designed specifically for home service businesses - intuitive enough for solo operators, powerful enough for growing teams, and affordable for everyone.
              </p>
              <p className="mb-0">
                Today, we're proud to serve businesses across the country, helping them save time, win more customers, and grow their revenue.
              </p>
            </Col>
            <Col lg={6}>
              <div className="p-5 rounded-3 text-center" style={{
                background: 'linear-gradient(135deg, #074a5a 0%, #053642 100%)',
                color: 'white'
              }}>
                <div className="mb-4">
                  <FontAwesomeIcon icon={faRocket} size="4x" style={{ color: '#ff871e' }} />
                </div>
                <h3 className="mb-3">Our Mission</h3>
                <p className="lead mb-0">
                  To provide home service businesses with affordable, powerful software that helps them compete with the big guys and grow their business on their own terms.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Our Values */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="display-5 fw-bold mb-3" style={{ color: '#074a5a' }}>
                Our Values
              </h2>
              <p className="lead text-muted">
                The principles that guide everything we do
              </p>
            </Col>
          </Row>

          <Row>
            {values.map((value, index) => (
              <Col key={index} lg={3} md={6} className="mb-4">
                <Card className="h-100 shadow-sm border-0 text-center p-4">
                  <Card.Body>
                    <div className="mb-3">
                      <FontAwesomeIcon
                        icon={value.icon}
                        size="3x"
                        style={{ color: '#074a5a' }}
                      />
                    </div>
                    <h4 className="mb-3">{value.title}</h4>
                    <p className="text-muted mb-0">{value.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Choose Us */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="display-5 fw-bold mb-3" style={{ color: '#074a5a' }}>
                Why Choose DHS CRM?
              </h2>
              <p className="lead text-muted">
                We understand home service businesses because we've worked with them
              </p>
            </Col>
          </Row>

          <Row>
            <Col lg={4} md={6} className="mb-4">
              <div className="d-flex align-items-start">
                <div className="flex-shrink-0 me-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#074a5a',
                      color: 'white'
                    }}>
                    <FontAwesomeIcon icon={faBullseye} size="lg" />
                  </div>
                </div>
                <div>
                  <h5 className="mb-2">Built for Your Industry</h5>
                  <p className="text-muted mb-0">
                    Every feature is designed specifically for home service businesses - no generic tools that don't fit.
                  </p>
                </div>
              </div>
            </Col>

            <Col lg={4} md={6} className="mb-4">
              <div className="d-flex align-items-start">
                <div className="flex-shrink-0 me-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#074a5a',
                      color: 'white'
                    }}>
                    <FontAwesomeIcon icon={faUsers} size="lg" />
                  </div>
                </div>
                <div>
                  <h5 className="mb-2">Dedicated Support</h5>
                  <p className="text-muted mb-0">
                    Real people who understand your business and are ready to help when you need it.
                  </p>
                </div>
              </div>
            </Col>

            <Col lg={4} md={6} className="mb-4">
              <div className="d-flex align-items-start">
                <div className="flex-shrink-0 me-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#074a5a',
                      color: 'white'
                    }}>
                    <FontAwesomeIcon icon={faStar} size="lg" />
                  </div>
                </div>
                <div>
                  <h5 className="mb-2">Constant Innovation</h5>
                  <p className="text-muted mb-0">
                    We're always adding new features based on customer feedback and industry trends.
                  </p>
                </div>
              </div>
            </Col>

            <Col lg={4} md={6} className="mb-4">
              <div className="d-flex align-items-start">
                <div className="flex-shrink-0 me-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#ff871e',
                      color: 'white'
                    }}>
                    <FontAwesomeIcon icon={faLightbulb} size="lg" />
                  </div>
                </div>
                <div>
                  <h5 className="mb-2">Easy to Use</h5>
                  <p className="text-muted mb-0">
                    Intuitive interface that your whole team can learn in minutes, not weeks.
                  </p>
                </div>
              </div>
            </Col>

            <Col lg={4} md={6} className="mb-4">
              <div className="d-flex align-items-start">
                <div className="flex-shrink-0 me-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#ff871e',
                      color: 'white'
                    }}>
                    <FontAwesomeIcon icon={faShieldAlt} size="lg" />
                  </div>
                </div>
                <div>
                  <h5 className="mb-2">Secure & Reliable</h5>
                  <p className="text-muted mb-0">
                    Bank-level security, automatic backups, and 99.9% uptime guarantee.
                  </p>
                </div>
              </div>
            </Col>

            <Col lg={4} md={6} className="mb-4">
              <div className="d-flex align-items-start">
                <div className="flex-shrink-0 me-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{
                      width: '60px',
                      height: '60px',
                      backgroundColor: '#ff871e',
                      color: 'white'
                    }}>
                    <FontAwesomeIcon icon={faHandshake} size="lg" />
                  </div>
                </div>
                <div>
                  <h5 className="mb-2">Fair Pricing</h5>
                  <p className="text-muted mb-0">
                    No contracts, no hidden fees, no surprises. Just honest pricing that grows with you.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="py-5" style={{ backgroundColor: '#074a5a', color: 'white' }}>
        <Container>
          <Row className="text-center">
            <Col lg={3} md={6} className="mb-4 mb-lg-0">
              <h2 className="display-4 fw-bold mb-2" style={{ color: '#ff871e' }}>500+</h2>
              <p className="lead mb-0">Happy Customers</p>
            </Col>
            <Col lg={3} md={6} className="mb-4 mb-lg-0">
              <h2 className="display-4 fw-bold mb-2" style={{ color: '#ff871e' }}>50K+</h2>
              <p className="lead mb-0">Appointments Scheduled</p>
            </Col>
            <Col lg={3} md={6} className="mb-4 mb-lg-0">
              <h2 className="display-4 fw-bold mb-2" style={{ color: '#ff871e' }}>$2M+</h2>
              <p className="lead mb-0">In Invoices Processed</p>
            </Col>
            <Col lg={3} md={6}>
              <h2 className="display-4 fw-bold mb-2" style={{ color: '#ff871e' }}>99.9%</h2>
              <p className="lead mb-0">Uptime Guarantee</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h2 className="display-5 fw-bold mb-4" style={{ color: '#074a5a' }}>
                Ready to Transform Your Business?
              </h2>
              <p className="lead text-muted mb-4">
                Join hundreds of home service businesses already growing with DHS CRM.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <a
                  href="/create-account"
                  className="btn btn-lg btn-secondary-custom rounded-pill px-5 py-3"
                  style={{ fontSize: '1.2rem' }}
                >
                  {DEFAULT_TRIAL_PERIOD_DAYS > 0 ? `Start Your ${DEFAULT_TRIAL_PERIOD_DAYS}-Day Free Trial` : 'Get Started'}
                </a>
                <a
                  href="#pricing"
                  className="btn btn-lg btn-outline-primary rounded-pill px-5 py-3"
                  style={{ fontSize: '1.2rem', borderColor: '#074a5a', color: '#074a5a' }}
                >
                  View Pricing
                </a>
              </div>
              {DEFAULT_TRIAL_PERIOD_DAYS > 0 && (
                <p className="mt-3 text-muted small">
                  <strong>✓ {DEFAULT_TRIAL_PERIOD_DAYS}-day free trial</strong> • No credit card required • Cancel anytime
                </p>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default About;
